﻿// track if search has been rendered only once
let searchRendered = false;
//Current version
// shared store
export function initEducationalDocumentsStore(Alpine) {
    Alpine.store('eduDocs', {
        initialized: false,  // true after initial data is loaded
        hasSearched: false,  // used to show/hide search and initial data
        countries: [],       // list of countries
        initialData: {},     // initial unfiltered data by country (same structure as countryData)
        countryData: {},     // { "Denmark": { medicines: { "Paracetamol": [{ name: string; description: string; url: string; documentName: string; }] }, page: number; pageSize: number; totalCount: number; } }
        loading: false,
        error: null
    });
}

export default function educationalDocuments() {
    return {
        searchQuery: '',
        expandedCountries: {},
        expandedMedicines: {},
        medicinePages: {},
        _debounce: null,
        _fetching: false,    // prevents overlapping retry chains
        _retryTimer: null,   // so we never stack retry timers
        showSearch: false,
        pageSize: 10,

        init() {
            if (!searchRendered) {
                searchRendered = true;
                this.showSearch = true;
            }

            if (!Alpine.store('eduDocs').initialized) {
                this.fetchInitialData();
            }
        },

        async fetchInitialData(attempt = 1) {
            const store = Alpine.store('eduDocs');
            if (store.initialized && Object.keys(store.initialData).length > 0) return;

            if (this._fetching) return;
            this._fetching = true;

            // Retry well under the 10s reappearance budget.
            const RETRY_DELAY = 3000;

            const scheduleRetry = (nextAttempt) => {
                store.loading = false;
                this._fetching = false;
                clearTimeout(this._retryTimer);
                this._retryTimer = setTimeout(() => this.fetchInitialData(nextAttempt), RETRY_DELAY);
            };

            store.loading = true;
            store.error = null;

            try {
                // One aggregated request instead of one-per-country. Falls back below if unavailable.
                const response = await fetch(
                    `/umbraco/api/educationaldocuments/searchall?query=*&pageSize=${this.pageSize}${attempt > 1 ? '&noCache=true' : ''}`
                );

                if (response.status === 503) {
                    console.warn(`Educational docs: index still building (attempt ${attempt}), retrying in ${RETRY_DELAY}ms`);
                    scheduleRetry(attempt + 1);
                    return;
                }

                let initialData;
                if (response.ok) {
                    const data = await response.json();
                    store.countries = data.countries || [];
                    initialData = this.buildDataMap(data.byCountry);
                } else {
                    // Aggregated endpoint unavailable — fall back to per-country loading.
                    initialData = await this.fetchInitialDataLegacy();
                }

                if (!initialData || Object.keys(initialData).length === 0) {
                    console.warn(`Educational docs: no data yet (attempt ${attempt}), retrying in ${RETRY_DELAY}ms`);
                    scheduleRetry(attempt + 1);
                    return;
                }

                store.initialData = initialData;
                store.initialized = true;
                store.loading = false;
                this._fetching = false;

            } catch (err) {
                console.warn(`Educational docs: error on attempt ${attempt}:`, err);
                scheduleRetry(attempt + 1);
            }
        },

        onSearchInput() {
            clearTimeout(this._debounce);
            const query = this.searchQuery.trim();

            if (query.length === 0) {
                Alpine.store('eduDocs').countryData = {};
                Alpine.store('eduDocs').hasSearched = false;
                this.medicinePages = {};
                return;
            }
            if (query.length < 2) return;

            this._debounce = setTimeout(() => this.performSearch(query), 300);
        },

        async performSearch(query) {
            const store = Alpine.store('eduDocs');
            this.medicinePages = {};
            store.loading = true;
            store.error = null;

            try {
                // One aggregated request instead of one-per-country. Falls back below if unavailable.
                const response = await fetch(
                    `/umbraco/api/educationaldocuments/searchall?query=${encodeURIComponent(query)}&pageSize=${this.pageSize}`
                );

                let countryData;
                if (response.ok) {
                    const data = await response.json();
                    store.countries = data.countries || [];
                    countryData = this.buildDataMap(data.byCountry);
                } else {
                    // Aggregated endpoint unavailable — fall back to per-country search.
                    const countriesResponse = await fetch('/umbraco/api/educationaldocuments/countries');
                    if (!countriesResponse.ok) throw new Error('Failed to fetch countries');
                    const countries = await countriesResponse.json();
                    store.countries = countries;
                    countryData = await this.fetchPerCountry(query, countries);
                }

                store.countryData = countryData;
                store.hasSearched = true;
            } catch (err) {
                store.error = 'An error occurred while searching. Please try again.';
                console.error('Error in search:', err);
            } finally {
                store.loading = false;
            }
        },

        transformToMedicines(results) {
            const medicines = {};

            for (const doc of results) {
                const { documentName, medicineName, files } = doc;

                // Never show a blank heading.
                const groupName = (medicineName && medicineName.trim())
                    || (documentName && documentName.trim())
                    || 'Other';

                if (!medicines[groupName]) medicines[groupName] = [];

                for (const file of files) {
                    medicines[groupName].push({
                        name: file.name,
                        description: file.description,
                        url: file.url,
                        documentName
                    });
                }
            }

            return medicines;
        },

        // Builds the { country: { medicines } } map from an aggregated /searchall payload.
        buildDataMap(byCountry) {
            const map = {};
            for (const country of Object.keys(byCountry || {})) {
                const entry = byCountry[country];
                if (!entry || !entry.results || entry.results.length === 0) continue;
                map[country] = { medicines: this.transformToMedicines(entry.results) };
            }
            return map;
        },

        // Legacy per-country fan-out, kept as a fallback so materials still load
        // if the aggregated endpoint is unavailable.
        async fetchPerCountry(query, countries) {
            const map = {};
            const promises = countries.map(async (country) => {
                const url = `/umbraco/api/educationaldocuments/search?query=${encodeURIComponent(query)}&country=${encodeURIComponent(country)}&page=1&pageSize=${this.pageSize}`;
                const response = await fetch(url);
                if (!response.ok) return null;
                const data = await response.json();
                return { country, data };
            });

            const results = await Promise.all(promises);
            for (const result of results) {
                if (!result || result.data.results.length === 0) continue;
                map[result.country] = { medicines: this.transformToMedicines(result.data.results) };
            }
            return map;
        },

        // Legacy initial load path: fetch countries, then fan out per country.
        async fetchInitialDataLegacy() {
            const store = Alpine.store('eduDocs');
            const countriesResponse = await fetch('/umbraco/api/educationaldocuments/countries');
            if (!countriesResponse.ok) return {};
            const countries = await countriesResponse.json();
            store.countries = countries;
            return this.fetchPerCountry('*', countries);
        },

        get initialized() {
            return Alpine.store('eduDocs').initialized;
        },
        get hasSearched() {
            return Alpine.store('eduDocs').hasSearched;
        },
        get initialData() {
            return Alpine.store('eduDocs').initialData;
        },
        get countryData() {
            return Alpine.store('eduDocs').countryData;
        },
        get loading() {
            return Alpine.store('eduDocs').loading;
        },
        get error() {
            return Alpine.store('eduDocs').error;
        },

        get activeData() {
            return this.hasSearched ? this.countryData : this.initialData;
        },

        get sortedCountryNames() {
            return Object.keys(this.activeData)
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        },

        get hasResults() {
            return Object.keys(this.activeData).length > 0;
        },

        toggleCountry(countryName) {
            this.expandedCountries[countryName] = !this.expandedCountries[countryName];
        },

        toggleMedicine(countryName, medicineName) {
            const key = `${countryName}-${medicineName}`;
            this.expandedMedicines[key] = !this.expandedMedicines[key];
        },

        isCountryExpanded(countryName) {
            return !!this.expandedCountries[countryName];
        },

        isMedicineExpanded(countryName, medicineName) {
            const key = `${countryName}-${medicineName}`;
            return !!this.expandedMedicines[key];
        },

        getSortedMedicines(countryName) {
            const data = this.activeData[countryName];
            return data
                ? Object.keys(data.medicines)
                    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                : [];
        },

        getAllFiles(countryName, medicineName) {
            const data = this.activeData[countryName];
            return data?.medicines?.[medicineName] || [];
        },

        getFiles(countryName, medicineName) {
            const all = this.getAllFiles(countryName, medicineName);
            const start = (this.medicineCurrentPage(countryName, medicineName) - 1) * this.pageSize;
            return all.slice(start, start + this.pageSize);
        },

        medicineCurrentPage(countryName, medicineName) {
            return this.medicinePages[`${countryName}-${medicineName}`] || 1;
        },

        medicineTotalPages(countryName, medicineName) {
            const total = this.getAllFiles(countryName, medicineName).length;
            return total > 0 ? Math.ceil(total / this.pageSize) : 0;
        },

        medicineHasPagination(countryName, medicineName) {
            return this.getAllFiles(countryName, medicineName).length > this.pageSize;
        },

        medicineCanGoPrev(countryName, medicineName) {
            return this.medicineCurrentPage(countryName, medicineName) > 1;
        },

        medicineCanGoNext(countryName, medicineName) {
            return this.medicineCurrentPage(countryName, medicineName) < this.medicineTotalPages(countryName, medicineName);
        },

        medicinePrevPage(countryName, medicineName) {
            if (this.medicineCanGoPrev(countryName, medicineName)) {
                this.medicinePages[`${countryName}-${medicineName}`] = this.medicineCurrentPage(countryName, medicineName) - 1;
            }
        },

        medicineNextPage(countryName, medicineName) {
            if (this.medicineCanGoNext(countryName, medicineName)) {
                this.medicinePages[`${countryName}-${medicineName}`] = this.medicineCurrentPage(countryName, medicineName) + 1;
            }
        }
    };
}